import type { ConnectorInfo } from '@hugo/core'
import { z } from 'zod'
import { relativeTime } from './format'

export type ConnectionState = 'unavailable' | 'disconnected' | 'connected' | 'error'

/** What the Connections card says about one connector. */
export type ConnectionView = { state: ConnectionState; text: string; error: string | null }

/** "synced 3 minutes ago", "synced just now" or "not synced yet". */
export function syncedLabel(lastSyncAt: string | null, now: Date): string {
  if (!lastSyncAt) return 'not synced yet'
  const when = relativeTime(new Date(lastSyncAt), now)
  return `synced ${when === 'now' ? 'just now' : when}`
}

export function describeConnection(info: ConnectorInfo, now: Date): ConnectionView {
  if (!info.available) return { state: 'unavailable', text: 'Coming soon', error: null }
  const connection = info.connection
  if (!connection || connection.status === 'disconnected') {
    return { state: 'disconnected', text: 'Not connected', error: null }
  }
  if (connection.status === 'error') {
    // The status line already says it; the reason may repeat it ("Reconnect needed: …").
    const error = connection.lastError?.replace(/^reconnect needed:\s*/i, '').trim() || null
    return { state: 'error', text: 'Reconnect needed', error }
  }
  return {
    state: 'connected',
    text: `Connected · ${connection.accountEmail} · ${syncedLabel(connection.lastSyncAt, now)}`,
    error: null,
  }
}

/** The API's error codes for "invalid input / do this first" (ValidationError). */
const LINK_ERROR_CODES = new Set(['validation', 'invalid_argument'])
const linkDetailsSchema = z.object({
  needsLink: z.literal(true),
  scopes: z.array(z.string()).catch([]),
})

/**
 * Whether a failed connect asks the user to give hugo access in Google first (link the signed-in
 * Google account with more scopes), and which scopes to ask for.
 */
export function googleLinkRequest(
  code: string,
  details: Record<string, unknown>,
): { scopes: string[] } | null {
  if (!LINK_ERROR_CODES.has(code)) return null
  const parsed = linkDetailsSchema.safeParse(details)
  return parsed.success ? { scopes: parsed.data.scopes } : null
}

const authorizeDetailsSchema = z.object({
  needsAuthorize: z.literal(true),
  url: z.string().regex(/^\/api\/connect\/[a-z]+$/),
})

/**
 * Whether a failed connect asks the user to grant hugo access at the provider first (GitHub and
 * the other OAuth connectors): where the browser goes for that. Only hugo's own connect route.
 */
export function authorizeRequest(
  code: string,
  details: Record<string, unknown>,
): { url: string } | null {
  if (!LINK_ERROR_CODES.has(code)) return null
  const parsed = authorizeDetailsSchema.safeParse(details)
  return parsed.success ? { url: parsed.data.url } : null
}

/** What the OAuth callback reported (`/settings/apps?connector=github&error=…`), in words. */
export function oauthErrorText(title: string, error: string): string {
  switch (error) {
    case 'access_denied':
      return `${title} access was not granted.`
    case 'state_mismatch':
      return `The ${title} sign-in expired or was started in another browser. Please try again.`
    case 'connector_unavailable':
      return `${title} is not set up on this server.`
    default:
      return `${title} did not answer as expected. Please try again.`
  }
}
